import {RouteOption} from './route-option';
import {BehaviorSubject} from "rxjs";

export class RouteOptions {
	constructor(
		readonly options: RouteOption[],
	) {
		// populate routeOptionsMap with non-defaults
		options.forEach((option) => {
			this.setNavigationValue(option, option.appliedValue);
		});
	}

	// Transient values representing the next state (for use in links)
	// currently only optional/matrix parameters
	private _navigationValues = new Map<string, string>();
	// Observable allows us to send the route options to routerLink in realtime with async pipe
	navigationValues = new BehaviorSubject(this._navigationValues);

	/**
	 * Get a specific instance from the options array.
	 * @param {{new(...args: never[]): T}} Class
	 * @return {T | undefined}
	 */
	getOption<T extends RouteOption>(Class: new (...args: never[]) => T): T | undefined {
		// assertion required
		return this.options.find(x => x instanceof Class) as T | undefined;
	}

	/**
	 * Can't know the type by the key, but you can still do instanceof after fetching.
	 * @param {string} key
	 * @return {RouteOption}
	 */
	getOptionByKey(key: string): RouteOption {
		return this.options.find(x => x.key === key) as RouteOption;
	}

	/**
	 * Set a Route Option value on `_navigationValues` for the next navigation
	 * @param {T} option
	 * @param {V | null} value
	 */
	setNavigationValue<
		T extends RouteOption, V extends T['appliedValue']
	>(option: T | undefined, value: V | null): void {
		if (option === undefined) {
			return;
		}

		// delete if no value, or it is the default value for this option
		if (value === null || option.isDefaultValue(value)) {
			this._navigationValues.delete(option.key);
		} else {
			// otherwise set the value
			this._navigationValues.set(
				option.key,
				option.toString(value)
			);
		}
		this.navigationValues.next(this._navigationValues);
	}

	/**
	 * Gets an array of changed options according to its comparator
	 * @param {RouteOptions} previousOptions
	 * @return {RouteOption[]}
	 */
	getChangedOptions(previousOptions?: RouteOptions): RouteOption[] {
		const changedOptions: RouteOption[] = [];
		this.options.forEach((option) => {
			if (!previousOptions) {
				changedOptions.push(option);
			} else {
				const previousOption = previousOptions.options.find(x => x instanceof option.constructor);
				if (!option.isEqual(option.appliedValue, previousOption?.appliedValue)) {
					changedOptions.push(option);
				}
			}
		});
		return changedOptions;
	}

}
